import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import { getFinancialErrorCode } from '#components/product-finance/errors';
import {
  formatProductDate,
  formatProductMoney,
} from '#components/product-finance/money';

import { EmptySection, SectionCard } from './dashboard-shared';

export function RecentFactsCard({
  dashboard,
  onEdit,
  onDelete,
  onUndo,
}: {
  dashboard: ReadyFinancialDashboard;
  onEdit: (
    movement: ReadyFinancialDashboard['recentMovements'][number],
  ) => void;
  onDelete: (
    actualFactId: string,
    options?: { removeLinkedContributions?: boolean },
  ) => Promise<void>;
  onUndo: (
    occurrenceKey: string,
    options?: { removeLinkedContributions?: boolean },
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [error, setError] = useState<MovementChangeError | null>(null);
  const movements = dashboard.recentMovements.filter(
    movement => filter === 'all' || movement.fact.type === filter,
  );

  async function deleteMovement(
    movement: ReadyFinancialDashboard['recentMovements'][number],
  ) {
    const message = movement.hasIncomeDistribution
      ? t('Delete this income and remove its goal contributions?')
      : movement.origin === 'emergency'
        ? t('Delete this expense and restore the amount to the emergency fund?')
        : t('Delete this movement?');
    if (!globalThis.confirm(message)) return;
    try {
      setError(null);
      await onDelete(
        movement.fact.id,
        movement.hasIncomeDistribution
          ? { removeLinkedContributions: true }
          : undefined,
      );
    } catch (changeError) {
      setError(classifyMovementChangeError(changeError));
    }
  }

  async function undoMovement(
    movement: ReadyFinancialDashboard['recentMovements'][number],
    occurrenceKey: string,
  ) {
    const message = movement.hasIncomeDistribution
      ? t('Undo this confirmation and remove its goal contributions?')
      : t('Undo this confirmation?');
    if (!globalThis.confirm(message)) return;
    try {
      setError(null);
      await onUndo(
        occurrenceKey,
        movement.hasIncomeDistribution
          ? { removeLinkedContributions: true }
          : undefined,
      );
    } catch (changeError) {
      setError(classifyMovementChangeError(changeError));
    }
  }

  return (
    <SectionCard title={<Trans>Recent movements</Trans>}>
      <View style={{ flexDirection: 'row', gap: 6, marginBottom: 8 }}>
        {(['all', 'income', 'expense'] as const).map(value => (
          <Button
            key={value}
            {...(filter === value ? { variant: 'primary' as const } : {})}
            onPress={() => setFilter(value)}
          >
            {value === 'all' ? (
              <Trans>All</Trans>
            ) : value === 'income' ? (
              <Trans>Incomes</Trans>
            ) : (
              <Trans>Expenses</Trans>
            )}
          </Button>
        ))}
      </View>
      {error !== null && (
        <p role="alert" style={{ color: theme.errorText }}>
          <MovementChangeErrorMessage error={error} />
        </p>
      )}
      {movements.length === 0 ? (
        <EmptySection>
          <Trans>No recent movements.</Trans>
        </EmptySection>
      ) : (
        movements.map(movement => (
          <View
            key={movement.fact.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 10,
              padding: '9px 0',
              borderBottom: `1px solid ${theme.tableBorder}`,
            }}
          >
            <View style={{ minWidth: 0 }}>
              <span>
                {movement.fact.description ?? <Trans>Movement</Trans>}
              </span>
              <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
                {formatProductDate(movement.fact.date)} ·{' '}
                {movement.origin === 'emergency' ? (
                  <Trans>Emergency expense</Trans>
                ) : movement.origin === 'spontaneous' ? (
                  <Trans>Quick movement</Trans>
                ) : movement.origin === 'legacy' ? (
                  <Trans>Legacy movement</Trans>
                ) : (
                  <Trans>Scheduled: {{ name: movement.occurrenceName }}</Trans>
                )}
              </span>
              {movement.hasIncomeDistribution && (
                <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
                  <Trans>
                    Linked to goal contributions. Editing keeps them; deleting
                    or undoing removes them.
                  </Trans>
                </span>
              )}
              {movement.origin === 'emergency' && movement.canEdit && (
                <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
                  <Trans>
                    Paid from the emergency fund. Editing or deleting adjusts
                    the fund.
                  </Trans>
                </span>
              )}
            </View>
            <View style={{ alignItems: 'flex-end', gap: 5 }}>
              <FinancialText
                as="strong"
                style={{
                  color:
                    movement.fact.type === 'income'
                      ? theme.noticeText
                      : theme.pageText,
                }}
              >
                {formatProductMoney(movement.fact.amount, movement.fact.type)}
              </FinancialText>
              <View style={{ flexDirection: 'row', gap: 5 }}>
                {movement.canEdit && (
                  <Button onPress={() => onEdit(movement)}>
                    <Trans>Edit</Trans>
                  </Button>
                )}
                {movement.canDelete && (
                  <Button onPress={() => void deleteMovement(movement)}>
                    <Trans>Delete</Trans>
                  </Button>
                )}
                {movement.canUndo && movement.occurrenceKey !== undefined && (
                  <Button
                    onPress={() =>
                      void undoMovement(movement, movement.occurrenceKey ?? '')
                    }
                  >
                    <Trans>Undo</Trans>
                  </Button>
                )}
              </View>
            </View>
          </View>
        ))
      )}
    </SectionCard>
  );
}

type MovementChangeError =
  | 'stale-movement'
  | 'stale-confirmation'
  | 'linked-conflict'
  | 'unknown';

function classifyMovementChangeError(error: unknown): MovementChangeError {
  switch (getFinancialErrorCode(error)) {
    case 'movement-not-editable':
    case 'movement-not-deletable':
      return 'stale-movement';
    case 'reconciliation-not-found':
      return 'stale-confirmation';
    case 'invalid-amount':
      return 'linked-conflict';
    default:
      return 'unknown';
  }
}

function MovementChangeErrorMessage({ error }: { error: MovementChangeError }) {
  switch (error) {
    case 'stale-movement':
      return <Trans>This movement can no longer be changed safely.</Trans>;
    case 'stale-confirmation':
      return <Trans>This confirmation no longer exists.</Trans>;
    case 'linked-conflict':
      return (
        <Trans>
          This change conflicts with the linked goal history or emergency fund
          balance.
        </Trans>
      );
    case 'unknown':
      return <Trans>We could not change this movement.</Trans>;
    default:
      return assertNever(error);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unexpected movement change error: ${String(value)}`);
}
